import { useCallback, useEffect, useRef } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { useCallActions } from '#features/calls/hooks/useCallActions';
import { hangupCall } from '#features/calls/model/calls';
import { useCallStore } from '#features/calls/model/callStore';
import { parseCallLink, type CallLink } from '#features/calls/utils/callLink';
import {
  dismissCallNotification,
  setCallWindowFlags,
} from '#features/notifications/utils/deviceSettings';
import { envConfig } from '#constants/envConfig';

// Capacitor keeps handing back the intent the app was last launched with, so a tap on a call
// notification from an hour ago is replayed on every plain launch from the home screen. Acting
// on it dismissed the notification of whatever call was ringing right then — which, since that
// notification is the ringtone on Android, silenced live calls half a second in.
const handledLinks = new Set<string>();

/**
 * Connects the Android incoming-call screen to the in-app call.
 *
 * Answer and Decline both open the app (see TeamplayMessagingService for why), arriving as a
 * `gg.teamplay.twa://call?callId=…&action=…` deep link. The app may have been launched by that
 * tap, so the call state is whatever the server says: resync() is what turns "some call id" into
 * a real ringing call the overlay can show, and only then is it worth answering or declining.
 *
 * Two things this deliberately waits for:
 *  - authentication. A cold start hands us the link before the session has been read, and
 *    answering without it would just fail — so the action is held until `isAuth` flips.
 *  - the server's view of the call. The ring may already be over (cancelled, timed out, or
 *    picked up on another device), in which case there is nothing to answer and we only take
 *    the system screen down.
 *
 * It also removes the system call screen whenever the in-app overlay takes over a call: a phone
 * with a live socket but a frozen WebView is rung twice on purpose, and once the app is showing
 * the call, the notification is a duplicate the user would have to dismiss by hand.
 *
 * No-op on web. Mount once, high in the tree.
 */
export const useNativeCallBridge = () => {
  const isAuth = useIsAuth();
  const { answer, resync } = useCallActions();
  // Held here rather than in state: a deep link that arrives before sign-in must survive
  // re-renders without triggering one.
  const pendingRef = useRef<CallLink | null>(null);

  const runPending = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) {
      return;
    }
    pendingRef.current = null;

    // Decline is answered immediately, by call id, without first asking the server what state
    // the call is in. Rejecting is a decision the user already made — making it wait on a
    // round trip meant that on a slow reply the app opened, the overlay showed the call still
    // ringing, and the caller kept ringing too.
    if (pending.action === 'decline') {
      void dismissCallNotification(pending.callId);
      hangupCall(pending.callId).catch(() => undefined);
      const current = useCallStore.getState();
      if (current.call?.callId === pending.callId) {
        current.setEnded('declined');
      }
      return;
    }

    await resync({
      callId: pending.callId,
      displayName: pending.callerName,
      avatar: pending.callerAvatar,
    });

    const store = useCallStore.getState();
    if (store.call?.callId !== pending.callId || store.phase !== 'incoming') {
      // Nothing to act on — and nothing to dismiss either: the notification on screen may
      // belong to a different, live call.
      // Already answered elsewhere, cancelled, or timed out — the overlay (or its absence)
      // is already correct, so acting on a stale tap would only end a call we just joined.
      return;
    }

    // 'open' is not a tap at all — the full-screen intent fires it by itself to bring the call
    // up, and treating it as handled would cancel the notification, which on Android is the
    // ringtone. Only answering takes it down here.
    if (pending.action === 'answer') {
      void dismissCallNotification(pending.callId);
      await answer();
    }
  }, [answer, resync]);

  useEffect(() => {
    if (!envConfig.IS_NATIVE || !isAuth) {
      return;
    }
    void runPending();
  }, [isAuth, runPending]);

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    let remove: (() => void) | undefined;
    let cancelled = false;

    void (async () => {
      const { App } = await import('@capacitor/app');

      const accept = (url: string | null | undefined) => {
        const link = parseCallLink(url);
        if (!link) {
          return;
        }
        const linkKey = `${link.callId}:${link.action}`;
        if (handledLinks.has(linkKey)) {
          return;
        }
        handledLinks.add(linkKey);
        pendingRef.current = link;
        if (isAuth) {
          void runPending();
        }
      };

      const handle = await App.addListener('appUrlOpen', ({ url }) => accept(url));
      if (cancelled) {
        void handle.remove();
        return;
      }
      remove = () => void handle.remove();

      // Cold start: the tap launched the app, so the link may have been delivered before this
      // listener existed.
      try {
        const launch = await App.getLaunchUrl();
        accept(launch?.url);
      } catch {
        // no launch url — normal for an app opened from the launcher
      }
    })();

    return () => {
      cancelled = true;
      remove?.();
    };
  }, [isAuth, runPending]);

  // Take the system call screen down only when this very call stops ringing — never for a
  // call id the store happens to be carrying. Dismissing on any other transition silenced the
  // ring half a second after it started: on this platform that notification IS the ringtone,
  // so cancelling it is cancelling the sound.
  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }
    return useCallStore.subscribe((state, previous) => {
      const ringingId = previous.phase === 'incoming' ? previous.call?.callId : undefined;
      if (!ringingId || state.phase === 'incoming') {
        return;
      }
      // Answered here, declined, cancelled by the caller or timed out — either way this call
      // no longer rings, and the notification for it must go.
      void dismissCallNotification(ringingId);
      if (state.phase !== 'active') {
        // Call over rather than picked up: stop being an app that shows itself above the
        // lock screen.
        void setCallWindowFlags(false);
      }
    });
  }, []);
};
